import { useState, useEffect } from "react";
import { useParams, useNavigate, generatePath } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  ImageList,
  ImageListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ImageWithLoader from "../../../common-components/ImageWithLoader";
import Avatar from "../../../common-components/Avatar";
import {
  getAdminVerificationDetail,
  reviewVerification,
} from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import { resolveImageUri } from "../../../utility/constants";

const verificationStatusColor = {
  NOT_SUBMITTED: "default",
  PENDING: "info",
  APPROVED: "success",
  REJECTED: "error",
  SUPERSEDED: "default",
};

const VerificationReviewDialog = ({
  open,
  onClose,
  decision,
  onConfirm,
  loading,
}) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const isReject = decision === "REJECTED";

  // Reset only when the dialog actually closes (Cancel, or a successful
  // confirm — the parent clears `dialog` on mutation success, not before),
  // so a failed submit leaves the typed reason in place instead of losing
  // it, and reopening after Cancel doesn't resurface stale text.
  useEffect(() => {
    if (!open) setRejectionReason("");
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isReject ? "Reject Verification" : "Approve Verification"}
      </DialogTitle>
      <DialogContent>
        {isReject && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (required)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={isReject ? "error" : "success"}
          disabled={loading || (isReject && !rejectionReason.trim())}
          onClick={() => onConfirm(rejectionReason)}
        >
          {loading ? <CircularProgress size={18} /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ImageGallery = ({ images, onOpen }) => {
  if (!images?.length) return null;
  return (
    <ImageList cols={Math.min(images.length, 3)} gap={8} sx={{ maxWidth: 480 }}>
      {images.map((url, i) => (
        <ImageListItem
          key={i}
          sx={{ cursor: "pointer" }}
          onClick={() => onOpen(url)}
        >
          <ImageWithLoader
            src={url}
            alt={`id-${i}`}
            sx={{ height: 140, borderRadius: "4px" }}
          />
        </ImageListItem>
      ))}
    </ImageList>
  );
};

const VerificationDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-verification", userId],
    queryFn: () => getAdminVerificationDetail(userId),
  });

  const mutation = useMutation({
    mutationFn: ({ status, rejectionReason }) =>
      reviewVerification(userId, { status, rejectionReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-verification", userId] });
      qc.invalidateQueries({ queryKey: ["admin-verifications"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setDialog(null);
      setSnackbar({ severity: "success", message: "Verification updated." });
    },
    onError: (err) =>
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      }),
  });

  if (isLoading) return <CircularProgress />;
  if (error || !data)
    return <Alert severity="error">Verification not found.</Alert>;

  const currentStatus = data.verificationStatus || "NOT_SUBMITTED";
  const userLink = generatePath(frontendRoutes.VIEW_USER, {
    userId: data.id || data._id,
  });
  const avatarUrl = data.profilePhoto?.path
    ? resolveImageUri(data.profilePhoto)
    : null;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(frontendRoutes.ADMIN_VERIFICATIONS)}
        sx={{ mb: 2 }}
      >
        Back to Verifications
      </Button>

      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <Avatar src={avatarUrl} name={data.name} size={48} />
        <Typography variant="h5" fontWeight={700}>
          {data.name}
        </Typography>
        <IconButton
          component="a"
          href={userLink}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          title="Open user profile in a new tab"
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={1}>
            Submission
          </Typography>
          <Typography variant="body2">Email: {data.email || "—"}</Typography>
          <Typography variant="body2">
            Phone: {data.phoneNumber || "—"}
          </Typography>
          <Typography variant="body2">
            ID type: {data.verifiedIdType || "—"}
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Chip
              label={currentStatus}
              color={verificationStatusColor[currentStatus]}
              size="small"
            />
            {data.identityVerificationRequired && (
              <Chip
                label="Flagged for verification"
                color="warning"
                size="small"
              />
            )}
          </Stack>
          {data.verifiedAt && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              Verified on {new Date(data.verifiedAt).toLocaleDateString()}
            </Typography>
          )}
          {data.verificationRejectionReason && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              Last rejection reason: {data.verificationRejectionReason}
            </Typography>
          )}
        </CardContent>
      </Card>

      {data.shops?.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography fontWeight={600} mb={1}>
              {data.shops.length > 1 ? "Shops" : "Shop"}
            </Typography>
            <Stack spacing={1.5}>
              {data.shops.map((shop) => {
                const shopId = shop.id || shop._id;
                const shopLink = shop.handle
                  ? generatePath(frontendRoutes.VIEW_SHOP, {
                      shopId: `@${shop.handle}`,
                    })
                  : generatePath(frontendRoutes.VIEW_SHOP, { shopId });
                const shopAvatarUrl = shop.profilePhoto?.path
                  ? resolveImageUri(shop.profilePhoto)
                  : null;
                return (
                  <Stack
                    key={shopId}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                  >
                    <Avatar
                      src={shopAvatarUrl}
                      name={shop.shopName}
                      size={36}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {shop.shopName}
                    </Typography>
                    {shop.isIdentityVerified && (
                      <Chip label="Verified" color="success" size="small" />
                    )}
                    <IconButton
                      component="a"
                      href={shopLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      title="Open shop in a new tab"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {currentStatus === "PENDING" &&
        data.verificationImageUrls?.length > 0 && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                ID Photos
              </Typography>
              <ImageGallery
                images={data.verificationImageUrls}
                onOpen={setLightboxUrl}
              />
            </CardContent>
          </Card>
        )}

      {currentStatus === "PENDING" && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="success"
            onClick={() => setDialog("APPROVED")}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDialog("REJECTED")}
          >
            Reject
          </Button>
        </Stack>
      )}

      {data.verificationHistory?.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography fontWeight={600} mb={1}>
              Previous Submissions
            </Typography>
            <Stack divider={<Divider />} spacing={2}>
              {[...data.verificationHistory].reverse().map((entry, i) => (
                <Box key={i}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    mb={0.5}
                  >
                    <Chip
                      label={entry.status}
                      color={verificationStatusColor[entry.status]}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {entry.idType || "—"}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Submitted{" "}
                    {entry.submittedAt
                      ? new Date(entry.submittedAt).toLocaleDateString()
                      : "—"}
                    {" · "}
                    Reviewed{" "}
                    {entry.reviewedAt
                      ? new Date(entry.reviewedAt).toLocaleDateString()
                      : "—"}
                    {entry.reviewedBy ? ` by ${entry.reviewedBy}` : ""}
                  </Typography>
                  {entry.rejectionReason && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Reason: {entry.rejectionReason}
                    </Typography>
                  )}
                  {entry.idImageUrls?.length > 0 && (
                    <Box mt={1}>
                      <ImageGallery
                        images={entry.idImageUrls}
                        onOpen={setLightboxUrl}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ p: 0 }}>
          <ImageWithLoader
            src={lightboxUrl}
            alt="ID photo"
            objectFit="contain"
            sx={{ width: "100%", height: { xs: 320, md: 480 } }}
          />
        </DialogContent>
      </Dialog>

      <VerificationReviewDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        decision={dialog}
        loading={mutation.isPending}
        onConfirm={(rejectionReason) =>
          mutation.mutate({ status: dialog, rejectionReason })
        }
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VerificationDetail;
